import React from 'react';
import { createPortal } from 'react-dom';
import { X, Star, Quote, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const QUOTE_MAX_LENGTH = 120;

const StoryDetailModal = ({ story, onClose }) => {
  if (!story) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10001] p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          {/* Image */}
          <div className="h-48 sm:h-56 overflow-hidden rounded-t-xl">
            <img
              src={story.image || 'https://via.placeholder.com/800x600?text=Vendor+Story'}
              alt={story.vendor_name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600?text=Vendor+Story';
              }}
            />
            <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium capitalize">
              {story.category}
            </div>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-yellow-500 fill-current" />
              <span className="text-lg font-semibold text-gray-900">{story.vendor_name}</span>
            </div>

            <div className="mb-4">
              <Quote className="w-6 h-6 text-gray-400 mb-2" />
              <p className="text-gray-700 italic leading-relaxed">"{story.quote}"</p>
            </div>

            <div className="border-t border-gray-200 pt-4 mb-4">
              <p className="text-sm font-semibold text-gray-900">{story.author}</p>
              {story.role && <p className="text-sm text-gray-600">{story.role}</p>}
            </div>

            {/* Stats */}
            {story.stats && (story.stats.bookings || story.stats.rating || story.stats.revenue) && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                {story.stats.bookings != null && (
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-sm font-semibold text-gray-900">{story.stats.bookings}</div>
                    <div className="text-xs text-gray-600">Bookings</div>
                  </div>
                )}
                {story.stats.rating != null && (
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-sm font-semibold text-gray-900 flex items-center justify-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      {story.stats.rating}
                    </div>
                    <div className="text-xs text-gray-600">Rating</div>
                  </div>
                )}
                {story.stats.revenue && (
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-sm font-semibold text-gray-900">{story.stats.revenue}</div>
                    <div className="text-xs text-gray-600">Revenue</div>
                  </div>
                )}
              </div>
            )}

            {/* View Vendor Button - always visible */}
            <Link
              to={story.vendor_id ? `/vendor/${story.vendor_id}` : '/browse-vendors'}
              className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              {story.vendor_id ? 'View Vendor Profile' : 'Browse Vendors'}
            </Link>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StoryDetailModal;
export { QUOTE_MAX_LENGTH };
